var View = require('views/base/view');
var BlocksView = require('views/block/block-collection-view');
var LocaleService = require('services/locale-service');
var ForgeService = require('services/forge-service');
var ContentService = require('services/content-service');

// # BlockView
//
// Base view to be used with all views that will render a page.
// All blocks have the following properties:
// * type: defines which type of block to load
// * class: a class to apply to the div that will wrap around the block
//
// Conventions:
// * page.type: used to define file paths
// ** the page views themselves should be located at /views/page/*TYPE*/*TYPE*-view
// ** all templates that might be rendered by the view should be located at /views/page/*TYPE*/templates/
// ** the default template is expected at /views/page/*TYPE*/templates/*TYPE*
// * page.template (optional): a template name other than the default
// ** if left undefined, the default template will be used
// ** must correspond to the name of another template in the templates folder for the view
// Initialization Flow and State Transitions:
// * Chaplin will call `initialize` where we hook `prePlay` to the `addedToDOM` event; `state: initialized`
// * `prePlay` calls load, which should be used by the page to add in DOM elements and such, but not to start anything playing; `state: loaded`
// * `prePlay` will also display the instructions, if available for the page.  Once the user is finished with them, `autoPlay` will be called.
//   If instructions are not available, `autoPlay` is called immediately.  `state: playing`
// * `play` should be on the pages themselves and is for actions when the page starts
// Methods for Inheriting Objects
// * load, play, mute, unmute, pause, resume, replay
var BlockView = View.extend({
  autoRender: true,
  listen:{
    'action:pause mediator': 'pause',
    'action:resume mediator': 'resume',
    'action:mute mediator': 'mute',
    'action:unmute mediator': 'unmute'
  },
  initialize: function(options) {
    _.bindAll(this, 'readyToGo');
    this.parent = options.parent;
    this.script = options.script;
    this.pageView = options.pageView;
    this.nestedLoads = [];

    if(this.pageView) {
      this.pageView.nested = [];
    }

    //TODO: using this when rendering nested views; want this value available in hbs templates
    this.model.set('cid', this.model.cid);
    var self = this;
    //TODO: we're kinda tied to blocks never being rendered outside of a collection, confirm that's OK
    this.listenTo(this.parent, 'addedToDOM', function() {
      if(self.model.get('tabindex') || self.model.get('tabindex') === 0) {
        self.$el.attr('tabindex', self.model.get('tabindex'));
      }
      self.load();
      self.applyAnimationProperty();
      self.applyAriaProperties();
      self.applyAttributeProperties();
      self.applyTabIndex();
      if(self.nestedLoads.length === 0) {
        if(self.model.get('state') === 'initialized') {
          self.setState('loaded');
        }
        self.trigger('block:state:loaded', self);
      } else {
        Promise.all(self.nestedLoads).then(() => {
          if(self.model.get('state') === 'initialized') {
            self.setState('loaded');
          }
          self.trigger('block:state:loaded', self);
        });
      }
    });
    this.listenTo(this.parent, 'page:state:change', this.onStateChange);
    this.setState('initialized');
    View.prototype.initialize.apply(this, arguments);
  },
  renderMultipleNestedBlocks: function(arrayName) {
    var self = this;
    // add the nested item type to array for this page
    if(this.pageView && !this.pageView.nested.includes(arrayName)) {
      this.pageView.nested.push(arrayName);
    }
    _.each(this.model.get(arrayName), function(item, index) {
      self.renderNestedBlocks(self.model.get('cid') + '-' + arrayName + '-' + index + '-blocks', item.blocks);
    });
  },
  renderNestedBlocks: function(blockRegionName, blocks) {
    if(!blocks) { return; }
    this.registerRegion(blockRegionName, '[data-region="' + blockRegionName + '"]');
    const nestedBlockPromise = ContentService.buildBlockCollection(blocks).then(blockCollection => {
      blockCollection.each((b) => b.set('order', this.model.get('order')));
      var blocksView = new BlocksView({
        collection: blockCollection,
        region: blockRegionName,
        parent: this,
        script: this.script,
        pageView: this.pageView
      });
      this.listenTo(blocksView, 'block:state:loaded', function(block) {
        this.trigger('block:state:loaded', block);
      });
      this.listenTo(blocksView, 'block:state:completed', function(block) {
        this.trigger('block:state:completed', block);
        if(this.$el && _.every(this.subviews, (sv) => !(sv instanceof BlocksView) || sv.isComplete())) {
          this.$el.addClass('all-completed');
        }
      });
      this.subview(blockRegionName, blocksView);
    });
    this.nestedLoads.push(nestedBlockPromise);
    return nestedBlockPromise;
  },
  reRender: function() {
    this.render();
    if(this.script && this.script.reRender) {
      this.script.reRender();
    }
  },
  onStateChange: function(parent) {
    var parentState = parent.model.get('state');
    //don't push completed parent state on children
    if(parentState !== 'completed') {
      if(parentState === 'playing' && parent.model.get('order')) {
        if(this.model.get('order') === parent.model.get('order')) {
          this.setState(parentState);
        }
      } else {
        this.setState(parentState);
      }
    }

    this.trigger('page:state:change', parent);
  },
  //TODO: figure out how these interact with the page-view and actionbar
  load: function() {},
  play: function() {},
  pause: function() {},
  resume: function() {},
  replay: function() {},
  reload: function() {},
  complete: function() {
    this.trigger('block:state:completed', this);
  },
  unload: function() {},
  mute: function() {},
  unmute: function() {},
  getTemplateFunction: function() {
    var templateName = this.model.get('template') ? this.model.get('template') : this.model.get('type');
    if(this.model.get('customTemplate')) {
      return require(this.model.get('customTemplate'));
    } else {
      return require('/views/block/' + this.model.get('type') + '/templates/' + templateName);
    }
  },
  readyToGo: function() {
    this.setState('completed');
  },
  setState: function(newState) {
    if(!this.model) return;
    var oldState = this.model.get('state');
    this.model.set('state', newState);
    this.$el.removeClass(oldState).addClass(newState);

    var stateListeners = {
      playing: this.play,
      paused: this.pause,
      completed: this.complete,
      unloading: this.unload
    };

    if(newState === 'playing' && oldState === 'paused') {
      this.resume();
    } else if(stateListeners[newState]) {
      _.bind(stateListeners[newState], this).call();
    }
  },
  isLoaded: function() {
    var selfLoaded = this.model.get('state') && this.model.get('state') !== 'initialized';
    if(selfLoaded && this.subviews) {
      return _.every(this.subviews, function(subview) {
        if(subview instanceof BlocksView) {
          return subview.isLoaded();
        } else {
          return true;
        }
      });
    } else {
      return selfLoaded;
    }
  },
  isComplete: function() {
    if(!this.model) return;
    var selfComplete = this.model.get('state') === 'completed';
    if(selfComplete && this.subviews) {
      return _.every(this.subviews, function(subview) {
        if(subview instanceof BlocksView) {
          return subview.isComplete();
        } else {
          return true;
        }
      });
    } else {
      return selfComplete;
    }
  },
  applyAnimationProperty: function() {
    if(this.model.get('animation')) {
      this.$el.attr('data-anijs', this.model.get('animation'));
    }
    if(this.model.get('onscroll')) {
      const scrollParams = this.model.get('onscroll');
      for(var prop in scrollParams) {
        if(scrollParams.hasOwnProperty(prop)) {
          if( prop === 'animation') {
            this.$el.attr('data-aos', scrollParams[prop]);
          } else {
            this.$el.attr('data-aos-'+prop, scrollParams[prop]);
          }
        }
      }
    }
  },
  applyAriaProperties: function() {
    if(this.model.get('aria')) {
      Object.keys(this.model.get('aria')).forEach(key => {
        const value = this.model.get('aria')[key];
        var prefix = '';
        if(key !== 'role') {
          prefix = 'aria-';
        }
        this.$el.attr(prefix + key, LocaleService.t(value));
      });
    }
  },

  renderChildAriaProperties: function(childItem, i, parentEl = this.$el) {
    if(childItem.hasOwnProperty('aria')) {
      Object.keys(childItem.aria).forEach((itemKey) => {
        const keyVal = childItem.aria[itemKey];
        let prefix = 'aria-';
        if( itemKey === 'role'){
          prefix = '';
        }
        parentEl.children().eq(i).attr(prefix + itemKey, LocaleService.t(keyVal));
      });
    }
  },

  /**
   * Applies an arbitrary collection of attributes to any block
   *
   * Notes:
   *   A value can be provided and the selector to find the element will default to [data-attributable]
   *
   *   An object containing a selector and a value can be provided for more control
   *
   *   The keys that describe the attribute name can be deeply nested and will be separated by dashes
   *     Example: { data: { test: { one: { two: 2}}} => data-test-one-two="2"
   *
   * Example:
   *   - type: text
   *     items:
   *       - description: some text
   *     attributes:
   *       data:
   *         value: 10              #=> yields data-value="10" with selector [data-attributable]
   *         info:
   *           - selector: '.item'  #=> yields data-info="10" with selector .item
   *             value: 10
   *       aria:
   *         label: some text       #=> yields aria-label="some text" with selector [data-attributable]
   */
  applyAttributeProperties: function() {
    const attributes = this.model.get('attributes');
    if(attributes) {
      (function flatten(attrs, path) {
        return Array.prototype.concat(...Object.entries(attrs).map(([key, value]) => {
          if(Array.isArray(value)) {
            const info = value.shift();
            return { attribute: `${path}-${key}`, value: info.value, selector: info.selector };
          } else if(typeof value === 'object') {
            return flatten(value, path ? `${path}-${key}` : key);
          } else {
            return  { attribute: `${path}-${key}`, value, selector: '[data-attributable]' };
          }
        }));
      })(attributes).forEach(info => {
        const { selector, attribute, value } = info;
        const elements = this.$el.parent().find(selector).attr(attribute, value);
      });
      this.$el.parent().find('*').removeAttr('data-attributable');
    }
  },
  applyTabIndex: function() {
    if(this.model.get('tabindex')) {
      this.$el.attr('tabindex', this.model.get('tabindex'));
    }
  }
});

module.exports = BlockView;
